import { memo, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader, PinInput, UnstyledButton } from '@mantine/core';

import { Button } from '../../../../../components/Atoms';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { PAGE_TYPE, PageType } from '../../constants';
import { useTwoFactorVerification } from './hooks/useTwoFactorVerification';

const ResendButton = memo<{
    isResendAvailable: boolean;
    resendCountdown: string | null;
    handleResend: () => void;
}>(({ isResendAvailable, resendCountdown, handleResend }) => {
    const { t } = useTranslation('auth');
    return (
        <div className="inline-flex items-center gap-1 mb-2">
            <span className="text-[14px] text-fg/78">{t('twoFactor.didntReceive')}</span>
            <UnstyledButton onClick={handleResend} disabled={!isResendAvailable}>
                <span
                    className={`text-[14px] ${
                        isResendAvailable
                            ? 'text-blue-400 light:text-blue-700 hover:underline cursor-pointer'
                            : 'cursor-not-allowed text-fg/50'
                    }`}
                >
                    {isResendAvailable
                        ? t('twoFactor.resend')
                        : t('twoFactor.resendCountdown', { countdown: resendCountdown })}
                </span>
            </UnstyledButton>
        </div>
    );
});

interface TwoFactorVerificationFormProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

function TwoFactorVerificationForm({ setPageType }: TwoFactorVerificationFormProps) {
    const { t } = useTranslation('auth');
    const errorMessage = useAppSelector((state) => state.error.message);

    const {
        isSubmitting,
        watch,
        handleOtpChange,
        isResendAvailable,
        resendCountdown,
        handleResend,
    } = useTwoFactorVerification({ setPageType });

    const handleCancel = useCallback(() => {
        setPageType(PAGE_TYPE.SIGN_IN);
    }, [setPageType]);

    const isVerifying = isSubmitting && watch('otpCode').length === 6;

    return (
        <form
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
            className="relative w-full flex flex-col justify-center items-center"
        >
            <div className="w-full flex flex-col justify-center items-center">
                <h1 className="text-base font-bold text-fg">{t('twoFactor.title')}</h1>
                <span className="text-sm text-fg/78">{t('twoFactor.verificationSentToEmail')}</span>
            </div>

            <div className="w-full flex flex-col justify-center items-center gap-6 my-4">
                <PinInput
                    size={'sm'}
                    length={6}
                    value={watch('otpCode')}
                    onChange={handleOtpChange}
                    disabled={isSubmitting}
                    styles={{
                        input: {
                            textAlign: 'center',
                        },
                    }}
                />

                <ResendButton
                    isResendAvailable={isResendAvailable}
                    resendCountdown={resendCountdown}
                    handleResend={handleResend}
                />
            </div>

            {errorMessage && (
                <p className="text-red-400 light:text-red-700 text-sm">{errorMessage}</p>
            )}

            <div className="w-full flex flex-row justify-center items-center gap-2">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={handleCancel}
                >
                    {t('twoFactor.cancel')}
                </Button>
            </div>

            {isVerifying && (
                <div className="absolute inset-0 z-10 flex justify-center items-center backdrop-blur-sm bg-black/30">
                    <Loader type="bars" size={'md'} />
                </div>
            )}
        </form>
    );
}

export default memo(TwoFactorVerificationForm);
